/**
 * Global motion-preference store.
 *
 * Lets visitors manually override the operating-system "prefers-reduced-motion"
 * setting from the site UI. The chosen preference is persisted to localStorage
 * and reflected on <html> via two classes consumed by index.css:
 *
 *   .reduce-motion  → blanket-disable animations & transitions (modal + page)
 *   .force-motion   → user explicitly opted INTO motion; neutralises the
 *                     per-component `prefers-reduced-motion` media queries so a
 *                     manual "Full motion" choice wins over the OS setting.
 */

export type MotionPref = "system" | "on" | "off";

const STORAGE_KEY = "sto-motion-pref";
const listeners = new Set<() => void>();

let pref: MotionPref = "system";

function readStored(): MotionPref {
  if (typeof window === "undefined") return "system";
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "on" || v === "off" ? v : "system";
  } catch {
    return "system";
  }
}

function systemPrefersReduce(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

export function getMotionPref(): MotionPref {
  return pref;
}

/** Whether reduced motion should currently be active given pref + OS setting. */
export function isReducedActive(): boolean {
  return pref === "on" || (pref === "system" && systemPrefersReduce());
}

function apply(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.toggle("reduce-motion", isReducedActive());
  root.classList.toggle("force-motion", pref === "off");
}

function emit(): void {
  listeners.forEach((l) => l());
}

export function setMotionPref(next: MotionPref): void {
  pref = next;
  try {
    if (next === "system") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* ignore persistence errors (private mode, etc.) */
  }
  apply();
  emit();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

let initialised = false;

/** Call once at startup to hydrate the preference and start watching the OS. */
export function initMotionPreference(): void {
  if (initialised || typeof window === "undefined") return;
  initialised = true;
  pref = readStored();
  apply();
  const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
  mq?.addEventListener?.("change", () => {
    apply();
    emit();
  });
}
